/** Pipeline bars: project count per stage group on one baseline, labels below. */
import React from 'react';
import { StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import type { Project } from '../../types/database';
import { useSharedPalette } from '../theme';
import { buildPipelineRows, pipelineBarPercent, pipelineColour } from './homeModels';

/** `showAll` adds the four tail stages beside the headline three; phones keep the three. */
export function PipelineChart({ projects, showAll = false, horizontal = false, pending=false, compact=false }: { projects: Project[]; showAll?: boolean; horizontal?: boolean;pending?:boolean;compact?:boolean }) {
  const palette = useSharedPalette();
  const {fontScale}=useWindowDimensions();
  const compactHorizontal = horizontal && fontScale <= 1.2;
  const allRows = buildPipelineRows(projects);
  const rows = showAll ? allRows : allRows.filter((r) => r.isKey);

  if(horizontal || fontScale>1.2)return <View style={[styles.largeWrap, compact && styles.compactWrap]}>
    {rows.map(row=><View key={row.key} accessible accessibilityLabel={`${row.label}, ${pending?'':row.count+' projects'}`} style={[styles.largeRow, compactHorizontal && styles.horizontalRow]}>
      <View style={[styles.largeHeading, compactHorizontal && styles.horizontalHeading]}><Text style={[styles.largeLabel,compactHorizontal && styles.horizontalLabel,{color:palette.foreground}]}>{row.label}</Text><Text style={[styles.largeCount,compactHorizontal && styles.horizontalCount,{color:palette.foreground}]}>{pending?'':row.count}</Text></View>
      <View style={[styles.largeTrack,compactHorizontal && styles.horizontalTrack,pending?{borderBottomWidth:StyleSheet.hairlineWidth,borderBottomColor:palette.border}:{backgroundColor:palette.track}]}>{!pending&&<View style={[styles.largeTrack,{width:`${pipelineBarPercent(row.count,allRows)}%`,backgroundColor:pipelineColour(row.key,palette)}]}/>}</View>
    </View>)}
  </View>;

  return (
    <View style={[styles.wrap,{paddingTop:28*fontScale}]}>
      <View style={[styles.plot, { borderBottomColor: palette.border }]}>
        {rows.map((row) => (
          <View key={row.key} style={styles.column}>
            {!pending&&row.count > 0 && (
              <View
                style={[
                  styles.bar,
                  { height: `${pipelineBarPercent(row.count, allRows)}%`, backgroundColor: pipelineColour(row.key, palette) },
                ]}
              >
                <Text style={[styles.count, { color: palette.foreground,top:-22*fontScale }]}>{row.count}</Text>
              </View>
            )}
          </View>
        ))}
      </View>
      <View style={styles.labels}>
        {rows.map((row) => (
          <Text key={row.key} style={[styles.label, { color: palette.muted }]} numberOfLines={fontScale>1.2?undefined:1}>{row.label}</Text>
        ))}
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  compactWrap: {paddingTop: 8, paddingBottom: 4, gap: 6},
  horizontalRow: { flexDirection: 'row', alignItems: 'center', gap: 12 },
  horizontalHeading: { width: 110 },
  horizontalLabel: { fontSize: 12 },
  horizontalCount: { fontSize: 12 },
  horizontalTrack: { flex: 1 },
  largeWrap:{paddingHorizontal:20,paddingTop:16,paddingBottom:12,gap:16},
  largeRow:{gap:8},
  largeHeading:{flexDirection:'row',alignItems:'baseline',gap:12},
  largeLabel:{fontSize:16,flex:1},
  largeCount:{fontSize:17,fontWeight:'600',fontVariant:['tabular-nums']},
  largeTrack:{height:8,borderRadius:4},
  wrap: { paddingHorizontal: 20, paddingTop: 28, paddingBottom: 12 },
  plot: { height: 76, flexDirection: 'row', alignItems: 'flex-end', gap: 20, borderBottomWidth: StyleSheet.hairlineWidth },
  column: { flex: 1, height: '100%', alignItems: 'center', justifyContent: 'flex-end' },
  bar: { width: '100%', maxWidth: 60, borderTopLeftRadius: 10, borderTopRightRadius: 10 },
  count: { position: 'absolute', top: -22, left: 0, right: 0, textAlign: 'center', fontSize: 16, fontWeight: '600' },
  labels: { flexDirection: 'row', gap: 20, marginTop: 8 },
  label: { flex: 1, textAlign: 'center', fontSize: 12 },
});
